import styled, { useTheme as styledTheme } from "styled-components";
import "../../App.css";
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ProjectDataProps,
  SelectOption,
  WorkComponentProps,
} from "../common/types";
import { CardContainer } from "../common/CardProps";
import SquareBtn from "../ui/Squarebtn";
import "../../styles/bubbleHover.scss";
import useMediaQuery from "../../hooks/useMediaQuerryHook";
import Parallax from "./Parallax";
import CardComponent from "./CardComponent";
import Select, { SingleValue } from "react-select";
import {
  filterOptions,
  getCustomStylesSingleSelect,
} from "../../utils/CustomizedLibraries/CustomSelectInput";
import { Images } from "../../config/ImagesExports";

const RecentText = styled.div`
  margin: 1.5rem 0 2rem 0;
  text-align: center;
  font-weight: bold;
  font-size: 1rem;
  @media (max-width: 768px) {
    margin-bottom: 0.5rem;
  }
`;

const NothingToDis = styled.div`
  border: 1px solid ${({ theme }) => theme.text};
  border-radius: 20px;
  width: 60%;
  @media (max-width: 576px) {
    width: 100%;
  }
  height: 10rem;
  padding: 1rem 0;
  margin: 5rem auto;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

export const NothingToDisplay = (
  <NothingToDis>There is nothing to display at the moment</NothingToDis>
);

function WorkComponent({
  projects,
  sideProjects = [],
  $isDark,
}: WorkComponentProps) {
  const styledComponentTheme = styledTheme();
  const [showSideProjects, setshowSideProjects] = useState(false);
  const [filter, setFilter] = useState<string>("all");
  const [sideProjectsFilter, setSideProjectsFilter] = useState<string>("all");
  const [selectedFilters, setSelectedFilters] = useState<SelectOption | null>({
    value: "all",
    label: "All",
  });
  const [selectedSideProjectsFilter, setSelectedSideProjectsFilter] =
    useState<SelectOption | null>({
      value: "all",
      label: "All",
    });

  const handleSelectFilterChange = (
    selectedOption: SingleValue<SelectOption>
  ) => {
    // if no option is selected set default to all
    if (!selectedOption) {
      setSelectedFilters({ value: "all", label: "All" });
      handleFilterClick("all");
    } else {
      //Set selected options
      setSelectedFilters(selectedOption);

      //Handle filter logic for each option
      switch (selectedOption.value) {
        case "all":
          handleFilterClick("all");
          break;
        case "design":
          handleFilterClick("design");
          break;
        case "web":
          handleFilterClick("web");
          break;
        case "mobile":
          handleFilterClick("mobile");
          break;
        default:
          handleFilterClick("all");
          break;
      }
    }
  };

  const handleSelectFilterSideProjectChange = (
    selectedOption: SingleValue<SelectOption>
  ) => {
    // if no option is selected set default to all
    if (!selectedOption) {
      setSelectedSideProjectsFilter({ value: "all", label: "All" });
      handleSideProjectFilterClick("all");
    } else {
      //Set selected options
      setSelectedSideProjectsFilter(selectedOption);

      //Handle filter logic for each option
      switch (selectedOption.value) {
        case "all":
          handleSideProjectFilterClick("all");
          break;
        case "design":
          handleSideProjectFilterClick("design");
          break;
        case "web":
          handleSideProjectFilterClick("web");
          break;
        case "mobile":
          handleSideProjectFilterClick("mobile");
          break;
        default:
          handleSideProjectFilterClick("all");
          break;
      }
    }
  };

  const filteredProjects =
    filter === "all"
      ? projects
      : projects.filter(
          (project) => project.type?.value.toLowerCase() === filter
        );

  const filteredSideProjects =
    sideProjectsFilter === "all"
      ? sideProjects
      : sideProjects.filter(
          (sidePrj) => sidePrj.type?.value.toLowerCase() === sideProjectsFilter
        );

  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = () => {
    navigate("/work");
  };

  const handleSideProjectBtnToggle = () => {
    setshowSideProjects(!showSideProjects);
  };

  const handleFilterClick = (type: string) => {
    setFilter(type.toLocaleLowerCase());
  };

  const handleSideProjectFilterClick = (type: string) => {
    setSideProjectsFilter(type.toLocaleLowerCase());
  };

  const handleProjectClick = (proj: ProjectDataProps) => {
    navigate(`/project-view/${proj._id}`, {
      state: {
        name: proj.name,
      },//pass name to state
    }); //fetch data from backend
  };

  const defaultImage = $isDark
    ? Images.placeholderLight
    : Images.placeholderDark;

  const isHomePage = location.pathname === "/";
  const displayMoreButton = isHomePage && projects.length > 2;
  const displayTwo = useMediaQuery(1024);
  const isMobile = useMediaQuery(576);
  const projectsToDisplayToHomePage = isHomePage
    ? displayTwo
      ? 2
      : 4
    : projects.length;
  const displaySideProjectsButton = !isHomePage && sideProjects?.length >= 1;

  return (
    <div>
      {isHomePage && (
        <Parallax offset={30}>
          {/* RECENT  */}
          <RecentText>WORK</RecentText>{" "}
        </Parallax>
      )}

      {!isHomePage && (
        <Parallax offset={80}>
          <div className="d-flex flex-column justify-content-center align-items-center">
            <div
              style={{ width: isMobile ? "100%" : "50%", marginTop: "1rem" }}
            >
              <label htmlFor="selectFilter">Filter Projects</label>
              <Select
                inputId="selectFilter"
                value={selectedFilters}
                onChange={handleSelectFilterChange}
                placeholder="Filter projects"
                options={filterOptions}
                styles={getCustomStylesSingleSelect(styledComponentTheme)}
                menuPortalTarget={document.body}
                isSearchable={false}
                menuPosition="absolute"
              />
            </div>
          </div>
        </Parallax>
      )}

      {filteredProjects.length > 0 ? (
        <CardContainer className="App-margin">
          {filteredProjects
            .slice(0, projectsToDisplayToHomePage)
            .map((project) => {
              const coverImage = project.galleryData.imgData.find(
                (img) => img.isCover
              );
              return (
                <CardComponent
                  key={project.id}
                  $imgUrl={coverImage?.src ?? defaultImage}
                  $projectName={project.name}
                  $projectYear={project.dateStarted ? project.dateStarted : 0}
                  $projectId={project.id}
                  $text={coverImage?.alt ?? "Anim image"}
                  onClick={() => handleProjectClick(project)}
                />
              );
            })}
        </CardContainer>
      ) : (
        NothingToDisplay
      )}

      {!isHomePage
        ? displaySideProjectsButton && (
            <Parallax>
              <div className="d-flex justify-content-center align-items-center App-margin">
                <SquareBtn
                  $btnText={showSideProjects ? "Close" : "Side projects"}
                  onClick={handleSideProjectBtnToggle}
                />
              </div>
            </Parallax>
          )
        : displayMoreButton && (
            <Parallax>
              <div className="d-flex justify-content-center align-items-center App-margin">
                <SquareBtn onClick={handleClick} $btnText="More work" />
              </div>
            </Parallax>
          )}

      {/* Archived projects */}
      {showSideProjects && (
        <>
          <Parallax offset={80}>
            <div className="d-flex flex-column justify-content-center align-items-center">
              <div style={{ width: isMobile ? "100%" : "50%" }}>
                <label htmlFor="selectSideFilter">Filter side projects</label>
                <Select
                  inputId="selectSideFilter"
                  value={selectedSideProjectsFilter}
                  onChange={handleSelectFilterSideProjectChange}
                  placeholder="Filter projects"
                  options={filterOptions}
                  styles={getCustomStylesSingleSelect(styledComponentTheme)}
                  menuPortalTarget={document.body}
                  isSearchable={false}
                  menuPosition="absolute"
                />
              </div>
            </div>
          </Parallax>

          {filteredSideProjects.length > 0 ? (
            <CardContainer className="App-margin">
              {filteredSideProjects.map((sidePrj) => {
                const coverImage = sidePrj.galleryData.imgData.find(
                  (img) => img.isCover
                );
                return (
                  <CardComponent
                    key={sidePrj.id}
                    $imgUrl={coverImage?.src ?? defaultImage}
                    $projectName={sidePrj.name}
                    $projectYear={sidePrj.dateStarted ? sidePrj.dateStarted : 0}
                    $projectId={sidePrj.id}
                    $text={coverImage?.alt ?? "Anim image"}
                    onClick={() => handleProjectClick(sidePrj)}
                  />
                );
              })}
            </CardContainer>
          ) : (
            NothingToDisplay
          )}
        </>
      )}
    </div>
  );
}

export default React.memo(WorkComponent);
